import {
  AuditEventDto,
  BrandDto,
  BrandKitDto,
  ClaimDto,
  CreateBrandDto,
  CreateClaimDto,
  CreateWorkspaceDto,
  InvitedMemberDto,
  InviteMemberDto,
  KnowledgeDocDto,
  KnowledgeRevisionDto,
  MemberDto,
  PlatformWorkspaceDto,
  SaveKnowledgeDto,
  SaveSiteDto,
  SiteDto,
  UpdateClaimDto,
  UpdateWorkspaceDto,
  WorkspaceDto,
} from "@growth/dto";
import { KnowledgeKind, MemberRole } from "@growth/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

const refreshMe = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ["me"] });

// ─── Workspace settings, brands, members ─────────────────────────────────────────────────────

export const useWorkspaceSettings = () =>
  useQuery({
    queryKey: ["workspace"],
    queryFn: async () => (await api.get<WorkspaceDto>("/workspace")).data,
  });

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateWorkspaceDto) =>
      (await api.patch<WorkspaceDto>("/workspace", input)).data,
    onSuccess: (workspace) => {
      queryClient.setQueryData(["workspace"], workspace);
      void refreshMe(queryClient);
    },
  });
};

export const useBrands = () =>
  useQuery({
    queryKey: ["brands"],
    queryFn: async () => (await api.get<BrandDto[]>("/brands")).data,
  });

export const useCreateBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBrandDto) => (await api.post<BrandDto>("/brands", input)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
      void refreshMe(queryClient);
    },
  });
};

export const useMembers = () =>
  useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<MemberDto[]>("/members")).data,
  });

export const useInviteMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: InviteMemberDto) =>
      (await api.post<InvitedMemberDto>("/members", input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });
};

/** Email a pending invite again. */
export const useResendInvite = () =>
  useMutation({
    mutationFn: async (id: string) =>
      (await api.post<InvitedMemberDto>(`/members/${id}/resend`)).data,
  });

export const useChangeRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: MemberRole }) =>
      (await api.patch<MemberDto>(`/members/${id}`, { role })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members"] });
      void refreshMe(queryClient);
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/members/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members"] });
      void refreshMe(queryClient);
    },
  });
};

export const useAuditLog = () =>
  useQuery({
    queryKey: ["audit"],
    queryFn: async () => (await api.get<AuditEventDto[]>("/workspace/audit")).data,
  });

// ─── Platform admins ─────────────────────────────────────────────────────────────────────────

export const usePlatformWorkspaces = () =>
  useQuery({
    queryKey: ["platform", "workspaces"],
    queryFn: async () => (await api.get<PlatformWorkspaceDto[]>("/platform/workspaces")).data,
  });

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWorkspaceDto) =>
      (await api.post<PlatformWorkspaceDto>("/platform/workspaces", input)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["platform", "workspaces"] });
      void refreshMe(queryClient);
    },
  });
};

// ─── The active brand: knowledge, claims, kit, blog ──────────────────────────────────────────

export const useKnowledge = () =>
  useQuery({
    queryKey: ["knowledge"],
    queryFn: async () => (await api.get<KnowledgeDocDto[]>("/knowledge")).data,
  });

export const useSaveKnowledge = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, ...input }: SaveKnowledgeDto & { kind: KnowledgeKind }) =>
      (await api.put<KnowledgeDocDto>(`/knowledge/${kind.toLowerCase()}`, input)).data,
    onSuccess: (doc) => {
      queryClient.setQueryData(["knowledge"], (docs: KnowledgeDocDto[] | undefined) =>
        docs?.map((existing: KnowledgeDocDto) => (existing.kind === doc.kind ? doc : existing)),
      );
      void queryClient.invalidateQueries({ queryKey: ["knowledge", "revisions", doc.kind] });
    },
  });
};

export const useRevisions = (kind: KnowledgeKind, enabled: boolean) =>
  useQuery({
    queryKey: ["knowledge", "revisions", kind],
    enabled,
    queryFn: async () =>
      (await api.get<KnowledgeRevisionDto[]>(`/knowledge/${kind.toLowerCase()}/revisions`)).data,
  });

export const useClaims = () =>
  useQuery({
    queryKey: ["claims"],
    queryFn: async () => (await api.get<ClaimDto[]>("/claims")).data,
  });

export const useSaveClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: (CreateClaimDto | UpdateClaimDto) & { id?: string }) =>
      id
        ? (await api.patch<ClaimDto>(`/claims/${id}`, input)).data
        : (await api.post<ClaimDto>("/claims", input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["claims"] }),
  });
};

export const useDeleteClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/claims/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["claims"] }),
  });
};

export const useBrandKit = () =>
  useQuery({
    queryKey: ["brand-kit"],
    queryFn: async () => (await api.get<BrandKitDto>("/brand-kit")).data,
  });

export const useSite = () =>
  useQuery({
    queryKey: ["site"],
    queryFn: async () => (await api.get<SiteDto | null>("/site")).data,
  });

export const useSaveSite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveSiteDto) => (await api.put<SiteDto>("/site", input)).data,
    onSuccess: (site) => {
      queryClient.setQueryData(["site"], site);
      void refreshMe(queryClient);
    },
  });
};
